import { useState } from 'react';

import { Typography, Image, Divider, Alert } from 'antd';
import { EditOutlined, DeleteOutlined } from '@ant-design/icons';

import {
  FormAddButton,
  FormDeleteButton,
  FormEditButton,
  StyledModal,
  WithLoading,
  EditButtonsSection,
  // FlagCountrySelectionFieldInput,
} from 'common/components';
import Marquee from 'react-fast-marquee';

import {
  useListenerMessageSaveCard,
  useVisibleModal,
  useMemberPaymentActions,
} from './hooks';

import { useCreditCardEula } from 'hooks/useCreditCardEula';
import useDocumentAttachEvent from 'hooks/documentAttachEventHook';
import { useCheckCreditCardPermission } from 'hooks/useCheckCreditCardPermission';

import { EVENT } from 'static/Constants';

import CreditCardImage from 'assets/credit-card.png';
import authorizeLogo from 'assets/credit-card/anetlogo.png';
import { useGetSavedMemberCard } from './hooks';
import { useCheckInvoicedPayment } from 'hooks/home/useCreditCardAlert';
import { useUserInfo } from 'hooks';

const MemberCardInformation = () => {
  const [visible, { onModal, offModal }] = useVisibleModal();
  const [isEdit, setEdit] = useState(false);

  const { userInfo } = useUserInfo();

  const [modeAction, setModeAction] = useState(null);

  const { savedMemberCard, isFetchingSavedCard, fetchSavedMemberCard } =
    useGetSavedMemberCard({
      modeAction,
    });

  useListenerMessageSaveCard({
    offModal,
    fetchSavedMemberCard,
    modeAction,
  });

  const { iframeUrl, loadingIframe, handleSaveCard, handleRemoveSavedCard } =
    useMemberPaymentActions({
      memberPaymentProfileId: savedMemberCard?.memberPaymentProfileId,
      fetchSavedMemberCard,
      offModal,
    });

  const { showCardEulaModal } = useCreditCardEula({
    context: 'billing',
  });

  const { canActionCard, hasDeleteCardPermission } =
    useCheckCreditCardPermission();

  const { isInvoicedTerm } = useCheckInvoicedPayment();

  const hasSavedMemberCard = Boolean(savedMemberCard?.memberPaymentProfileId);

  const onEditCard = () => {
    showCardEulaModal({ when: 'edit-card' });
  };

  const onAddCard = () => {
    showCardEulaModal({ when: 'add-card' });
  };

  const startEditCard = () => {
    setModeAction('edit');
    onModal();
    handleSaveCard({ modeAction: 'edit' });
  };

  const startAddCard = () => {
    setModeAction('add');
    onModal();
    handleSaveCard({ modeAction: 'add' });
  };

  const onAcceptCreditCardEula = (e) => {
    const when = e?.detail?.when;

    const actionCollection = {
      'add-card': startAddCard,
      'edit-card': startEditCard,
    };

    actionCollection[when]?.();
  };

  useDocumentAttachEvent({
    name: EVENT.ACCEPT_CARD_EULA,
    handler: onAcceptCreditCardEula,
    dependencies: [savedMemberCard?.memberPaymentProfileId],
  });

  const canDeleteCard = hasDeleteCardPermission && userInfo?.member?.id === 1;

  return (
    <>
      <div>
        <WithLoading loading={isFetchingSavedCard}>
          <div className='member-tab-billing__section-edit-title'>
            <Typography.Text strong style={{ fontSize: 20 }}>
              Credit Card Information
            </Typography.Text>
            {(canActionCard || canDeleteCard) && (
              <EditButtonsSection
                isEdit={isEdit}
                onEdit={() => setEdit(true)}
                onCancel={() => {
                  setEdit(false);
                }}
                buttons={['cancel', canActionCard ? 'edit' : '']}
              />
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Image
              src={CreditCardImage}
              width={70}
              height={70}
              preview={false}
            />
            <>
              {hasSavedMemberCard ? (
                <>
                  <Typography.Text
                    strong
                    style={{ color: '#6c757d', fontSize: 15 }}
                  >
                    {savedMemberCard?.nameAlias ?? 'xxxx card'}
                  </Typography.Text>

                  {isEdit ? (
                    <div style={{ gap: 3, display: 'flex' }}>
                      {canActionCard ? (
                        <FormEditButton
                          icon={<EditOutlined />}
                          onClick={onEditCard}
                          size='small'
                          text={null}
                        />
                      ) : null}
                      {canDeleteCard ? (
                        <FormDeleteButton
                          icon={<DeleteOutlined />}
                          size='small'
                          text={null}
                          onClick={handleRemoveSavedCard}
                        />
                      ) : null}
                    </div>
                  ) : null}
                </>
              ) : (
                <EmptyCardRender
                  isEdit={isEdit}
                  onAddCard={onAddCard}
                  isInvoicedTerm={isInvoicedTerm}
                />
              )}
            </>
          </div>
        </WithLoading>
      </div>

      {visible && (
        <CreditCardIframeModal
          title={modeAction === 'edit' ? 'Edit Credit Card' : 'Add Credit Card'}
          visible={visible}
          onCancel={() => {
            offModal();
            setModeAction(null);
          }}
        >
          <WithLoading loading={loadingIframe}>
            <CreditCardIframe iframeUrl={iframeUrl} />
          </WithLoading>
        </CreditCardIframeModal>
      )}
    </>
  );
};

export const CreditCardIframeModal = (props) => {
  const { children, ...rest } = props;

  return (
    <StyledModal
      wrapClassName='member-payment__modal'
      width={1000}
      footer={<CreditCardFooter />}
      maskClosable={false}
      {...rest}
    >
      <CreditCardHeader />
      {children}
    </StyledModal>
  );
};

export const CreditCardIframe = ({ iframeUrl, ...rest }) => {
  return (
    <div className='scroller' style={{ height: '100%', width: '100%' }}>
      <iframe
        loading='lazy'
        width='960'
        height='560'
        src={iframeUrl}
        title='Save Credit Card'
        frameBorder='0'
        {...rest}
      ></iframe>
    </div>
  );
};

const EmptyCardRender = ({ isEdit, onAddCard, isInvoicedTerm }) => {
  if (isEdit) {
    return <FormAddButton text='Add Bank Card' ghost onClick={onAddCard} />;
  }

  return (
    <div style={{ width: 500 }}>
      <AlertEmptyCard isInvoicedTerm={isInvoicedTerm} />
    </div>
  );
};

export const AlertEmptyCard = ({ isInvoicedTerm = false }) => {
  let message = isInvoicedTerm
    ? 'With an invoiced term, no credit card is needed.'
    : 'To continue accessing all our service features, please add a credit card to your account.';

  return (
    <Alert
      banner
      message={
        <Marquee pauseOnHover gradient={false} speed={30}>
          <div style={{ marginRight: 100 }}>{message}</div>
        </Marquee>
      }
    />
  );
};

const CreditCardHeader = () => {
  return (
    <div style={{ textAlign: 'right', paddingRight: 32 }}>
      <Image width={140} src={authorizeLogo} preview={false} />
    </div>
  );
};

const CreditCardFooter = () => {
  return (
    <>
      <div className='member-payment__modal--footer'>
        <div>
          <Typography.Text strong style={{ fontSize: 16 }}>
            VAULT has partnered with
          </Typography.Text>
          <Image
            style={{ padding: '0 4px' }}
            width={106}
            src={authorizeLogo}
            preview={false}
          />
          <Typography.Text strong style={{ fontSize: 16 }}>
            to store your payment methods securely
          </Typography.Text>
        </div>
      </div>
      <div>
        <Divider
          style={{
            width: '50%',
            minWidth: '50%',
            margin: '10px auto',
            borderTop: '2px solid #006d75',
          }}
        />
      </div>
    </>
  );
};

export default MemberCardInformation;
